import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import times from 'lodash/times';
import Styled from '~/components/Styled/Styled';
import PlayerHeadshot from '~/components/PlayerHeadshot/PlayerHeadshot';
import css from '~/components/Bixby/BixbyKeyPlayersCard/BixbyKeyPlayersCard.scss';
import Link from '~/components/Link/Link';
import { ResponsivePNG } from 'sports-react';

const getTeamLogo = player => get(player, 'teams[0].logos.tiny', '');
const getTeamName = player => get(player, 'teams[0].full_name', '');

const renderHeader = header => (
  <div className={css.header}>
    <div className={css.headerText}>{header}</div>
  </div>
);

const renderStats = (player, stats) =>
  stats.map(s => (
    <div className={css.playerStat} key={`${player.id}-${s.label}`}>
      <div className={css.statInfo}>{s.stat}</div>
      <div className={css.statLabel}>{s.label}</div>
    </div>
  ));

const renderStars = stars => (
  <div className={css.starsContainer}>
    <div className={css.stars}>
      {times(stars, i => (
        <img
          key={`star-${i}`}
          className={css.star}
          src="/static/vectors/star.svg"
          alt=""
          aria-hidden="true"
        />
      ))}
    </div>
  </div>
);

const renderPlayer = (player, stats, stars = 0, isLast) => (
  <div className={css.playerContainer} key={player.id}>
    <Link route={player.api_uri}>
      <a aria-label={`${player.full_name} player page`}>
        <div className={css.playerRow}>
          <div className={css.playerInfo}>
            <div className={css.left}>
              <div className={css.columnContainer}>
                <ResponsivePNG
                  className={css.teamLogo}
                  src={getTeamLogo(player)}
                  alt={getTeamName(player)}
                />
                <div className={css.playerNumber}>{player.number}</div>
              </div>
            </div>
            <div className={css.imageContainer}>
              <PlayerHeadshot player={player} size="bixby" />
            </div>
            <div className={css.right}>
              <div className={css.columnContainer}>
                <div className={css.playerName}>{player.full_name}</div>
                <div className={css.playerStats}>{renderStats(player, stats)}</div>
              </div>
            </div>
          </div>
          {stars > 0 && renderStars(stars)}
        </div>
      </a>
    </Link>
    {!isLast && <hr className={css.playerDivider} />}
  </div>
);

const renderPlayers = (players, stats, stars = false) =>
  players.map((player, i) =>
    renderPlayer(player, stats[i], stars ? i + 1 : 0, players.length === i + 1)
  );

// eslint-disable-next-line react/prefer-stateless-function
class KeyPlayersCard extends React.Component {
  render() {
    const { players, stats, playersByHeaders, title, starPlayers } = this.props;
    if (
      isEmpty(playersByHeaders) &&
      players.every(e => !Object.keys(e) || Object.keys(e).length === 1)
    ) {
      return null;
    }
    if (isEmpty(playersByHeaders)) {
      const playersToRender = starPlayers ? players.slice(0, 3) : players;
      return (
        <div className={css.root}>
          <h2 className={css.title}>{title}</h2>
          <div className={css.playersContainer}>
            {renderPlayers(playersToRender, stats, starPlayers)}
          </div>
        </div>
      );
    }
    return (
      <div className={css.root}>
        <h2 className={css.title}>{title}</h2>
        {Object.keys(playersByHeaders).map(header => {
          const playersAndStats = playersByHeaders[header];
          const playerList = playersAndStats.players;
          const statList = playersAndStats.stats;
          return (
            <div key={header}>
              {renderHeader(header)}
              {renderPlayers(playerList, statList)}
            </div>
          );
        })}
      </div>
    );
  }
}

/*
  This class requires either players and stats or playersByHeaders.

  Players by headers should be a map with the keys being headers,
  and the values being an object with a list of players and a list of stats
  ex: playersByHeaders: { "Passing": {players: [], stats: [] } }
*/

const playersShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    headshots: PropTypes.object,
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        full_name: PropTypes.string,
        colour_1: PropTypes.string,
        logos: PropTypes.object,
      })
    ),
    position_abbreviation: PropTypes.string,
  })
);

KeyPlayersCard.propTypes = {
  players: playersShape,
  stats: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        stat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    )
  ),
  title: PropTypes.string.isRequired,
  starPlayers: PropTypes.bool,
  playersByHeaders: PropTypes.object,
};

KeyPlayersCard.defaultProps = {
  players: [],
  stats: [],
  starPlayers: false,
  playersByHeaders: {},
};

KeyPlayersCard.displayName = 'BixbyKeyPlayersCard';

export default Styled(KeyPlayersCard, [css]);
